import React, { forwardRef } from 'react';
import { Menu } from '@headlessui/react';
import { motion } from 'framer-motion';
import { SERIF, SANS, todayTokens } from '../../theme/todayTokens';

const AvatarUserMenu = forwardRef(function AvatarUserMenu(
  {
    currentUser,
    userRole,
    isDarkMode,
    logoutLoading,
    onNavigate,
    onLogout,
  },
  ref
) {
  const t = todayTokens(isDarkMode);

  const displayName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Account';

  const menuItems = [
    { id: 'profile', label: 'Profile', path: '/profile' },
    { id: 'account', label: 'Account Security', path: '/account-management' },
  ];

  if (userRole !== 'landlord') {
    menuItems.push({ id: 'settings', label: 'Settings', path: '/settings' });
  }

  const menuButtonStyle = (active, danger = false) => ({
    display: 'block',
    width: 'calc(100% - 16px)',
    margin: '0 8px',
    padding: '10px 12px',
    fontFamily: SANS,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'left',
    border: 'none',
    borderRadius: '10px',
    cursor: logoutLoading && danger ? 'wait' : 'pointer',
    backgroundColor: active
      ? danger
        ? isDarkMode
          ? 'rgba(240, 113, 74, 0.14)'
          : 'rgba(185, 58, 18, 0.08)'
        : isDarkMode
          ? 'rgba(143, 163, 224, 0.12)'
          : 'rgba(28, 43, 90, 0.06)'
      : 'transparent',
    color: danger ? t.rust : t.ink,
  });

  return (
    <Menu.Items
      ref={ref}
      className="absolute right-0 z-50 mt-2 w-64 focus:outline-none topbar-v2__avatar-menu"
      style={{
        borderRadius: '18px',
        backgroundColor: t.surface,
        border: `1px solid ${t.line}`,
        boxShadow: isDarkMode
          ? '0 24px 48px rgba(0, 0, 0, 0.45)'
          : '0 24px 48px rgba(0, 0, 0, 0.12)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${t.lineSoft}`,
          minWidth: 0,
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: SERIF,
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.15,
            color: t.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </p>
        {currentUser?.email && (
          <p
            style={{
              margin: '4px 0 0',
              fontFamily: SANS,
              fontSize: 13,
              color: t.muted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentUser.email}
          </p>
        )}
      </div>

      <div style={{ padding: '8px 0' }}>
        {menuItems.map((item) => (
          <Menu.Item key={item.id}>
            {({ active }) => (
              <button
                type="button"
                style={menuButtonStyle(active)}
                onClick={() => onNavigate(item.path)}
              >
                {item.label}
              </button>
            )}
          </Menu.Item>
        ))}
      </div>

      <div
        style={{
          padding: '8px 0',
          borderTop: `1px solid ${t.lineSoft}`,
        }}
      >
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              style={menuButtonStyle(active, true)}
              onClick={onLogout}
              disabled={logoutLoading}
            >
              {logoutLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      border: `2px solid ${t.rust}`,
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                    }}
                  />
                  Logging out…
                </span>
              ) : (
                'Sign out'
              )}
            </button>
          )}
        </Menu.Item>
      </div>
    </Menu.Items>
  );
});

export default AvatarUserMenu;
